import axios from '../../utils/axiosConfig';

// Action Types
export const CREATE_EVENT_SUCCESS = 'CREATE_EVENT_SUCCESS';
export const CREATE_EVENT_FAIL = 'CREATE_EVENT_FAIL';

export const GET_EVENTS_REQUEST = 'GET_EVENTS_REQUEST';
export const GET_EVENTS_SUCCESS = 'GET_EVENTS_SUCCESS';
export const GET_EVENTS_FAIL = 'GET_EVENTS_FAIL';

export const GET_EVENT_REQUEST = 'GET_EVENT_REQUEST';
export const GET_EVENT_SUCCESS = 'GET_EVENT_SUCCESS';
export const GET_EVENT_FAIL = 'GET_EVENT_FAIL';

export const GET_PROMOTER_EVENTS_REQUEST = 'GET_PROMOTER_EVENTS_REQUEST';
export const GET_PROMOTER_EVENTS_SUCCESS = 'GET_PROMOTER_EVENTS_SUCCESS';
export const GET_PROMOTER_EVENTS_FAIL = 'GET_PROMOTER_EVENTS_FAIL';

export const GET_EVENT_MESSAGES_REQUEST = 'GET_EVENT_MESSAGES_REQUEST';
export const GET_EVENT_MESSAGES_SUCCESS = 'GET_EVENT_MESSAGES_SUCCESS';
export const GET_EVENT_MESSAGES_FAIL = 'GET_EVENT_MESSAGES_FAIL';

export const CREATE_EVENT_MESSAGE_REQUEST = 'CREATE_EVENT_MESSAGE_REQUEST';
export const CREATE_EVENT_MESSAGE_SUCCESS = 'CREATE_EVENT_MESSAGE_SUCCESS';
export const CREATE_EVENT_MESSAGE_FAIL = 'CREATE_EVENT_MESSAGE_FAIL';

export const DELETE_EVENT_MESSAGE_REQUEST = 'DELETE_EVENT_MESSAGE_REQUEST';
export const DELETE_EVENT_MESSAGE_SUCCESS = 'DELETE_EVENT_MESSAGE_SUCCESS';
export const DELETE_EVENT_MESSAGE_FAIL = 'DELETE_EVENT_MESSAGE_FAIL';

export const SEARCH_EVENTS_REQUEST = 'SEARCH_EVENTS_REQUEST';
export const SEARCH_EVENTS_SUCCESS = 'SEARCH_EVENTS_SUCCESS';
export const SEARCH_EVENTS_FAIL = 'SEARCH_EVENTS_FAIL';

export const UPDATE_EVENT_SUCCESS = 'UPDATE_EVENT_SUCCESS';
export const UPDATE_EVENT_FAIL = 'UPDATE_EVENT_FAIL';
export const CANCEL_EVENT_SUCCESS = 'CANCEL_EVENT_SUCCESS';
export const CANCEL_EVENT_FAIL = 'CANCEL_EVENT_FAIL';
export const PUBLISH_EVENT_SUCCESS = 'PUBLISH_EVENT_SUCCESS';
export const DELETE_EVENT_SUCCESS = 'DELETE_EVENT_SUCCESS';
export const DELETE_EVENT_FAIL = 'DELETE_EVENT_FAIL';

export const INVITE_BRAND_REQUEST = 'INVITE_BRAND_REQUEST';
export const INVITE_BRAND_SUCCESS = 'INVITE_BRAND_SUCCESS';
export const INVITE_BRAND_FAIL = 'INVITE_BRAND_FAIL';

export const GET_TEMPLATE_EVENTS_REQUEST = 'GET_TEMPLATE_EVENTS_REQUEST';
export const GET_TEMPLATE_EVENTS_SUCCESS = 'GET_TEMPLATE_EVENTS_SUCCESS';
export const GET_TEMPLATE_EVENTS_FAIL = 'GET_TEMPLATE_EVENTS_FAIL';

export const DUPLICATE_EVENT_REQUEST = 'DUPLICATE_EVENT_REQUEST';
export const DUPLICATE_EVENT_SUCCESS = 'DUPLICATE_EVENT_SUCCESS';
export const DUPLICATE_EVENT_FAIL = 'DUPLICATE_EVENT_FAIL';

// Actions
// `draft` saves the event unpublished. It rides in the query string because
// the API decides the Easypay gate before it parses the multipart body.
export const createEvent =
  (eventData, { draft = false } = {}) =>
  async (dispatch, getState) => {
    const { auth } = getState();
    const token = auth.token;
    try {
      const res = await axios.post('/events', eventData, {
        params: draft ? { draft: 1 } : undefined,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      dispatch({
        type: CREATE_EVENT_SUCCESS,
        payload: res.data,
      });
      return res.data;
    } catch (err) {
      dispatch({
        type: CREATE_EVENT_FAIL,
        payload:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err.message,
      });
      // Rethrow so the form can show accurate feedback (e.g. upload too large).
      throw err;
    }
  };

// Module-level counter bumped on every non-append (reset) request. Each
// response carries its captured value; responses older than the reducer's
// latestResetId are dropped. Combined with AbortController below, in-flight
// requests from a previous sort/filter are cancelled AND any that still land
// are discarded — keeping the list free of stale pollution.
let eventsResetCounter = 0;
let inFlightControllers = [];

const abortAllInFlight = () => {
  inFlightControllers.forEach((c) => {
    try {
      c.abort();
    } catch (e) {}
  });
  inFlightControllers = [];
};

export const getAllEvents =
  ({
    page = 1,
    pageSize = 12,
    sortBy,
    sortOrder,
    hidePastEvents,
    hideLiveEvents,
    append = false,
    lang,
  } = {}) =>
  async (dispatch, getState) => {
    if (!append) {
      // A new sort/filter (or initial load) invalidates every in-flight
      // request from the previous generation.
      abortAllInFlight();
      eventsResetCounter += 1;
    }
    const resetId = eventsResetCounter;

    const controller = new AbortController();
    inFlightControllers.push(controller);

    dispatch({ type: GET_EVENTS_REQUEST, meta: { append, resetId } });

    const { auth } = getState();
    const token = auth.token;

    const params = new URLSearchParams();
    params.set('page', page);
    params.set('pageSize', pageSize);
    if (sortBy) params.set('sortBy', sortBy);
    if (sortOrder) params.set('sortOrder', sortOrder);
    if (hidePastEvents) params.set('hidePastEvents', 'true');
    if (hideLiveEvents) params.set('hideLiveEvents', 'true');
    if (lang) params.set('lang', lang);

    try {
      const res = await axios.get(`/events?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        signal: controller.signal,
      });

      // Support both new and old shapes
      const payload =
        res.data && Array.isArray(res.data)
          ? { data: res.data, pagination: null, append }
          : {
              data: res.data?.data ?? [],
              pagination: res.data?.pagination ?? null,
              append,
            };

      dispatch({
        type: GET_EVENTS_SUCCESS,
        payload,
        meta: { append, resetId },
      });
    } catch (err) {
      // Swallow cancellations — they're intentional, not errors.
      if (
        axios.isCancel?.(err) ||
        err?.name === 'CanceledError' ||
        err?.name === 'AbortError' ||
        err?.code === 'ERR_CANCELED'
      ) {
        return;
      }
      dispatch({
        type: GET_EVENTS_FAIL,
        payload: err.response?.data?.message || 'Failed to fetch events',
        meta: { resetId },
      });
    } finally {
      inFlightControllers = inFlightControllers.filter((c) => c !== controller);
    }
  };

export const getEvent =
  (eventId, { lang } = {}) =>
  async (dispatch, getState) => {
    dispatch({ type: GET_EVENT_REQUEST });

    const token = getState().auth.token;

    try {
      const params = {};
      if (lang) params.lang = lang;

      const res = await axios.get(`/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      dispatch({ type: GET_EVENT_SUCCESS, payload: res.data });
      return res.data;
    } catch (err) {
      dispatch({
        type: GET_EVENT_FAIL,
        payload: err.response?.data?.message || 'Failed to fetch event details',
      });
      throw err;
    }
  };

export const getEventBySlug =
  (eventSlug, { lang } = {}) =>
  async (dispatch, getState) => {
    dispatch({ type: GET_EVENT_REQUEST });

    const token = getState().auth.token;

    try {
      const params = {};
      if (lang) params.lang = lang;

      const res = await axios.get(`/events/by-slug/${eventSlug}`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      dispatch({ type: GET_EVENT_SUCCESS, payload: res.data });
      return res.data;
    } catch (err) {
      dispatch({
        type: GET_EVENT_FAIL,
        payload: err.response?.data?.message || 'Failed to fetch event details',
      });
      throw err;
    }
  };

// Per-placement availability for the floorplan. Backend is the source of
// truth — the brand UI must paint markers from this response, not from a
// locally filtered bookings list.
export const getFloorplanAvailability = (eventId) => async () => {
  try {
    const res = await axios.get(`/events/${eventId}/floorplan-availability`);
    return res.data;
  } catch {
    return { eventId, placements: [], occupiedPlacementIds: [] };
  }
};

// actions/events.js
// Fetch events belonging to the authenticated promoter ("my events").
// Backend infers the promoter from req.user, so no promoterId is sent.
export const getPromoterEvents =
  (opts = {}) =>
  async (dispatch) => {
    dispatch({ type: GET_PROMOTER_EVENTS_REQUEST });

    const params = new URLSearchParams();
    if (opts.withSpaces) params.set('withSpaces', '1');
    if (opts.page) params.set('page', String(opts.page));
    if (opts.pageSize) params.set('pageSize', String(opts.pageSize));
    if (opts.sortBy) params.set('sortBy', String(opts.sortBy));
    if (opts.sortOrder) params.set('sortOrder', String(opts.sortOrder));
    if (opts.lang) params.set('lang', String(opts.lang));

    const qs = params.toString();
    const url = `/events/promoter-events${qs ? `?${qs}` : ''}`;

    try {
      const res = await axios.get(url);
      const list = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      dispatch({ type: GET_PROMOTER_EVENTS_SUCCESS, payload: list });
      return list;
    } catch (err) {
      dispatch({
        type: GET_PROMOTER_EVENTS_FAIL,
        payload:
          err.response?.data?.message || 'Failed to fetch promoter events',
      });
      throw err;
    }
  };

// Fetch events for a specific promoter (public — no auth required).
// Does NOT touch the `promoterEvents` Redux slice so it can't pollute
// the dashboard "My Events" list. Returns the array directly.
export const getEventsByPromoterId =
  (promoterId, opts = {}) =>
  async () => {
    if (!promoterId) return [];

    const params = new URLSearchParams();
    if (opts.withSpaces) params.set('withSpaces', '1');
    if (opts.page) params.set('page', String(opts.page));
    if (opts.pageSize) params.set('pageSize', String(opts.pageSize));
    if (opts.sortBy) params.set('sortBy', String(opts.sortBy));
    if (opts.sortOrder) params.set('sortOrder', String(opts.sortOrder));
    if (opts.lang) params.set('lang', String(opts.lang));

    const qs = params.toString();
    const url = `/events/by-promoter/${promoterId}${qs ? `?${qs}` : ''}`;

    const res = await axios.get(url);
    return Array.isArray(res.data) ? res.data : res.data?.data ?? [];
  };

export const updateEvent =
  (eventId, eventData, { refreshList = true, lang } = {}) =>
  async (dispatch, getState) => {
    const { auth } = getState();
    const token = auth.token;
    try {
      const res = await axios.put(`/events/${eventId}`, eventData, {
        headers:
          eventData instanceof FormData
            ? { Authorization: `Bearer ${token}` }
            : { Authorization: `Bearer ${token}` },
      });

      dispatch({ type: UPDATE_EVENT_SUCCESS, payload: res.data });

      if (refreshList && auth?.user?.id) {
        await dispatch(
          getPromoterEvents({
            withSpaces: true,
            lang,
            page: 1,
            pageSize: 100,
          })
        );
      }

      return res.data;
    } catch (err) {
      dispatch({
        type: UPDATE_EVENT_FAIL,
        payload: err.response?.data?.message || 'Failed to update event',
      });
      throw err;
    }
  };

// Promoter cancels their own event (T2B-402). The API refuses (409
// EVENT_HAS_BOOKINGS) once any brand holds a live booking, so callers must
// surface the rejection rather than assume success — the button's own
// precondition can be stale by the time the request lands.
export const cancelEvent =
  (eventId, { refreshList = true, lang } = {}) =>
  async (dispatch, getState) => {
    const { auth } = getState();
    const token = auth.token;
    try {
      const res = await axios.post(
        `/events/${eventId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Patches the same lists UPDATE_EVENT_SUCCESS does, so the card flips to
      // Cancelled without waiting for the refetch below.
      dispatch({
        type: CANCEL_EVENT_SUCCESS,
        payload: {
          id: Number(eventId),
          status: res.data?.status ?? 'Cancelled',
        },
      });

      if (refreshList && auth?.user?.id) {
        await dispatch(
          getPromoterEvents({
            withSpaces: true,
            lang,
            page: 1,
            pageSize: 100,
          })
        );
      }

      return res.data;
    } catch (err) {
      dispatch({
        type: CANCEL_EVENT_FAIL,
        payload: err.response?.data?.message || 'Failed to cancel event',
      });
      throw err;
    }
  };

// Draft → live. The API refuses without an Easypay account (403
// EASYPAY_ACCOUNT_REQUIRED) or without future dates (409 EVENT_DATE_*); the
// error is rethrown so the caller can say which.
export const publishEvent = (eventId) => async (dispatch, getState) => {
  const token = getState().auth.token;
  const res = await axios.post(
    `/events/${eventId}/publish`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  // Patches the same lists CANCEL/UPDATE do, so the card leaves the Drafts tab
  // straight away.
  dispatch({
    type: PUBLISH_EVENT_SUCCESS,
    payload: { id: Number(eventId), status: res.data?.status ?? 'Pending' },
  });
  return res.data;
};

// Drafts only — the API refuses anything else (409 EVENT_NOT_DRAFT). Rethrows
// so the caller can say whether it worked.
export const deleteEvent = (eventId) => async (dispatch, getState) => {
  const { auth } = getState();
  const token = auth.token;
  try {
    await axios.delete(`/events/${eventId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    dispatch({
      type: DELETE_EVENT_SUCCESS,
      // Numeric, to match the ids in the lists the reducer filters.
      payload: Number(eventId),
    });
  } catch (err) {
    dispatch({
      type: DELETE_EVENT_FAIL,
      payload:
        err?.response?.data?.message || err?.message || 'Failed to delete event',
    });
    throw err;
  }
};
export const getEventMessages = (eventId) => async (dispatch, getState) => {
  dispatch({ type: GET_EVENT_MESSAGES_REQUEST, meta: { eventId } });
  const { auth } = getState();
  const token = auth.token;
  try {
    const res = await axios.get(`/eventMessages/${eventId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    dispatch({
      type: GET_EVENT_MESSAGES_SUCCESS,
      payload: res.data,
      meta: { eventId },
    });
  } catch (err) {
    dispatch({
      type: GET_EVENT_MESSAGES_FAIL,
      payload: err.response?.data?.message || 'Failed to fetch event messages',
      meta: { eventId },
    });
  }
};

// Create a new message for an event
export const createEventMessage =
  (eventId, message) => async (dispatch, getState) => {
    dispatch({ type: CREATE_EVENT_MESSAGE_REQUEST, meta: { eventId } });
    const { auth } = getState();
    const token = auth.token;
    try {
      const res = await axios.post(
        '/eventMessages',
        { eventId, message },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      dispatch({
        type: CREATE_EVENT_MESSAGE_SUCCESS,
        payload: res.data,
        meta: { eventId },
      });
    } catch (err) {
      dispatch({
        type: CREATE_EVENT_MESSAGE_FAIL,
        payload:
          err.response?.data?.message || 'Failed to create event message',
        meta: { eventId },
      });
    }
  };

// Delete an event message
export const deleteEventMessage =
  (messageId, eventId) => async (dispatch, getState) => {
    dispatch({ type: DELETE_EVENT_MESSAGE_REQUEST, meta: { eventId } });
    const { auth } = getState();
    const token = auth.token;
    try {
      await axios.delete(`/eventMessages/${messageId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      dispatch({
        type: DELETE_EVENT_MESSAGE_SUCCESS,
        payload: messageId,
        meta: { eventId },
      });
    } catch (err) {
      dispatch({
        type: DELETE_EVENT_MESSAGE_FAIL,
        payload:
          err.response?.data?.message || 'Failed to delete event message',
        meta: { eventId },
      });
    }
  };

// search events
export const searchEvents =
  (filters = {}) =>
  async (dispatch, getState) => {
    // Search is always a "fresh list" operation — invalidate everything
    // else in flight (including list fetches) and bump the generation.
    abortAllInFlight();
    eventsResetCounter += 1;
    const resetId = eventsResetCounter;

    const controller = new AbortController();
    inFlightControllers.push(controller);

    dispatch({ type: SEARCH_EVENTS_REQUEST, meta: { resetId } });

    const { auth } = getState();
    const token = auth.token;

    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && Array.isArray(value) && value.length > 0) {
        queryParams.append(key, value.join(','));
      } else if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });

    try {
      const response = await axios.get(
        `/events/search?${queryParams.toString()}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          signal: controller.signal,
        }
      );

      // search currently returns an array; keep legacy behavior
      dispatch({
        type: SEARCH_EVENTS_SUCCESS,
        payload: Array.isArray(response.data)
          ? response.data
          : response.data?.data ?? [], // future-proof if you paginate search later
        meta: { resetId },
      });
    } catch (error) {
      if (
        axios.isCancel?.(error) ||
        error?.name === 'CanceledError' ||
        error?.name === 'AbortError' ||
        error?.code === 'ERR_CANCELED'
      ) {
        return;
      }
      dispatch({
        type: SEARCH_EVENTS_FAIL,
        payload: error.response?.data?.message || 'Failed to search events',
        meta: { resetId },
      });
    } finally {
      inFlightControllers = inFlightControllers.filter((c) => c !== controller);
    }
  };

export const getTemplateEvents =
  ({
    page = 1,
    pageSize = 20,
    sortBy = 'createdAt',
    sortOrder = 'DESC',
  } = {}) =>
  async (dispatch, getState) => {
    dispatch({ type: GET_TEMPLATE_EVENTS_REQUEST });

    const token = getState().auth.token;

    const params = new URLSearchParams();
    params.set('page', page);
    params.set('pageSize', pageSize);
    params.set('sortBy', sortBy);
    params.set('sortOrder', sortOrder);

    try {
      const res = await axios.get(`/events/templates?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
      const pagination = res.data?.pagination ?? null;

      dispatch({
        type: GET_TEMPLATE_EVENTS_SUCCESS,
        payload: { data, pagination },
      });

      return { data, pagination };
    } catch (err) {
      dispatch({
        type: GET_TEMPLATE_EVENTS_FAIL,
        payload:
          err.response?.data?.message || 'Failed to fetch template events',
      });
      throw err;
    }
  };

export const duplicateEvent = (eventId) => async (dispatch, getState) => {
  dispatch({ type: DUPLICATE_EVENT_REQUEST });

  const token = getState().auth.token;

  try {
    const res = await axios.post(
      `/events/${eventId}/duplicate`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );

    dispatch({ type: DUPLICATE_EVENT_SUCCESS, payload: res.data });

    // Refresh promoter events list
    const userId = getState().auth?.user?.id;
    if (userId) {
      dispatch(
        getPromoterEvents({
          withSpaces: true,
          page: 1,
          pageSize: 100,
        })
      );
    }

    return res.data;
  } catch (err) {
    dispatch({
      type: DUPLICATE_EVENT_FAIL,
      payload: err.response?.data?.message || 'Failed to duplicate event',
    });
    throw err;
  }
};

export const inviteBrandToEvent =
  (eventId, brandId) => async (dispatch, getState) => {
    dispatch({ type: INVITE_BRAND_REQUEST });

    const token = getState().auth.token;

    try {
      const res = await axios.post(
        `/events/${eventId}/invite-brand`,
        { brandId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      dispatch({
        type: INVITE_BRAND_SUCCESS,
        payload: res.data,
      });

      return res.data;
    } catch (err) {
      dispatch({
        type: INVITE_BRAND_FAIL,
        payload: err.response?.data?.message || 'Failed to send invitation',
      });
      throw err;
    }
  };

// Email invites (any address, with or without a brand account). The Invite
// tab keeps the list in local state, so these don't touch the store.
export const getEventInvites = (eventId) => async (_dispatch, getState) => {
  const token = getState().auth.token;
  const res = await axios.get(`/events/${eventId}/invites`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const sendEventInvite =
  (eventId, email, lang) => async (_dispatch, getState) => {
    const token = getState().auth.token;
    const res = await axios.post(
      `/events/${eventId}/invites`,
      { email, lang },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return res.data;
  };
